import { Api } from '@shared-providers/api.provider';
import { Injectable } from '@angular/core';
import { Provider } from '@shared-providers/Provider';
import { LoadingService } from '@shared-services/loading.service';
import { IDateRange, IStatistic, IStatisticParams } from '@shared-libs/interfaces';
import { format } from 'date-fns';
import { encodeQueryUrl } from '@shared-libs/helpers';

@Injectable({
	providedIn: 'root',
})
export class StatisticProvider extends Provider<IStatistic> {
	protected readonly url: string = '/statistics';

	constructor(
		protected readonly api: Api,
		protected readonly loadingService: LoadingService
	) {
		super(loadingService);
	}

	public getDigibankReport(): Promise<any> {
		return this.process(this.api.post(`${this.url}/digibank/export`, {}));
	}

	public getActivitiesExport(dateRange: IDateRange): Promise<any> {
		return this.process(
			this.api.download(
				`${this.url}/activities/export?dateRange=${JSON.stringify(dateRange)}`,
				`Activiteiten_Export_${format(new Date(), 'dd-MM-yyyy')}.xlsx`
			)
		);
	}

	public getAnonymousAttendancesExport(): Promise<void> {
		return this.process(
			this.api.download(
				`${this.url}/attendances/export`,
				`anonieme_bezoekers_${format(new Date(), 'dd-MM-yyyy')}.xlsx`
			)
		) as any as Promise<void>;
	}

	public getEventDaysExport(): Promise<void> {
		return this.process(
			this.api.download(`${this.url}/event-days/export`, `activiteiten_${format(new Date(), 'dd-MM-yyyy')}.xlsx`)
		) as any as Promise<void>;
	}

	public getImpactCountStatistic(
		params: IStatisticParams
	): Promise<{ total: number; loans: number; essentialServices: number; educations: number }> {
		return this.process(
			this.api.get(`${this.url}/impact/count?query=${encodeQueryUrl({ filter: params })}`)
		) as Promise<any>;
	}

	public getActivitiesOnLocationsStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/activities/locations?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getActivitiesOnEventTypesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/activities/event-types?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getActivitiesOnAgenciesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/activities/agencies?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getActivitiesOnAttendanceTypesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/activities/attendance-types?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getActivitiesOnDigibankObjectivesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/activities/digibank-objectives?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getActivitiesCountStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/activities/count?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getClientsCountStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/clients/count?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getClientsOnZipcodesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/clients/zipcodes?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getClientsOnTargetAudiencesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/clients/target-audiences?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getClientsOnDevicesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/clients/devices?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getClientsOnGendersStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/clients/genders?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getClientsOnOriginsStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/clients/origins?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getClientsOnAgeCategoriesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/clients/age-categories?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getClientsOnAttendancesCountStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/clients/attendances-count?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getDevicesOnStatusesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/devices/statuses?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getDevicesOnDeviceTypesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/devices/device-types?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getLoansCountStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/loans/count?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getAttendancesOnQuestionCategoriesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/question-categories?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnEventTypesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/event-types?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnAttendanceTypesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/attendance-types?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnAgenciesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/agencies?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnDigibankObjectivesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/digibank-objectives?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnLocationsStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/locations?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnAttendanceStatusesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/attendance-statuses?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnZipcodesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/zipcodes?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnTargetAudiencesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/target-audiences?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnDevicesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/devices?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnOriginsStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/origins?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnAgeCategoriesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/age-categories?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesOnGendersStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/attendances/genders?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAttendancesAverageStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/attendances/average?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getAttendancesCountStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/attendances/count?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getAnonymousAttendancesCountStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/anonymous-attendances/count?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getAnonymousAttendancesOnLocationsStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/anonymous-attendances/locations?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAnonymousAttendancesOnEventTypesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/anonymous-attendances/event-types?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getAnonymousAttendancesOnAgenciesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/anonymous-attendances/agencies?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsCountStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/cancellations/count?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getCancellationsOnReasonsStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/reasons?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnTargetAudiencesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/target-audiences?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnAgeCategoriesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/age-categories?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnAgenciesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/agencies?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnAttendanceTypesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/attendance-types?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnDigibankObjectivesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/digibank-objectives?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnEventTypesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/event-types?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnGendersStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/genders?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnLocationsStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/locations?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnOriginsStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/origins?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getCancellationsOnZipcodesStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/cancellations/zipcodes?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}

	public getDigichequesUsageStatistic(): Promise<IStatistic> {
		return this.process(this.api.get(`${this.url}/beego/digicheques`), false) as Promise<IStatistic>;
	}

	public getDigichequesOnZipcodesStatistic(): Promise<IStatistic> {
		return this.process(this.api.get(`${this.url}/beego/digicheques/zipcodes`), false) as Promise<IStatistic>;
	}

	public getRatingsAverageStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/ratings/average?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getRatingsCountStatistic(params: IStatisticParams): Promise<number> {
		return this.process(
			this.api.get(`${this.url}/ratings/count?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<number>;
	}

	public getRatingsScoreStatistic(params: IStatisticParams): Promise<IStatistic> {
		return this.process(
			this.api.get(`${this.url}/ratings/score?query=${encodeQueryUrl({ filter: params })}`),
			false
		) as Promise<IStatistic>;
	}
}
